import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { marked } from 'marked';
import { Download, Pencil, Trash2 } from 'lucide-react';
import { useSkill, useDeleteSkill } from '@/features/skills/hooks';
import { downloadSkillApi } from '@/features/skills/api';
import { useAuth } from '@/features/auth/AuthContext';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/components/ui/Toast';
import { getApiErrorMessage } from '@/lib/api-client';

export default function SkillDetailPage() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const query = useSkill(id);
  const remove = useDeleteSkill();
  const [downloading, setDownloading] = useState(false);

  if (query.isLoading)
    return (
      <div className="flex justify-center py-12 text-text-muted">
        <Spinner size={24} />
      </div>
    );
  if (query.isError || !query.data) {
    return <p className="text-red-400">Skill não encontrada.</p>;
  }

  const s = query.data;
  const canEdit = user?.role === 'admin' || s.uploader.id === user?.id;

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const { blob, filename } = await downloadSkillApi(s.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Falha ao baixar a skill.'));
    } finally {
      setDownloading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm(`Confirma excluir a skill "${s.name}"?`)) return;
    try {
      await remove.mutateAsync(s.id);
      toast.success('Skill excluída.');
      nav('/skills');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Falha ao excluir.'));
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h1 className="text-2xl font-semibold">{s.name}</h1>
                <Badge tone="brand">v{s.version}</Badge>
              </div>
              <p className="text-text-secondary max-w-2xl">{s.description}</p>
              <div className="mt-3 flex flex-wrap gap-1">
                {s.tags.map((t) => (
                  <Badge key={t.id} tone="neutral">
                    #{t.name}
                  </Badge>
                ))}
              </div>
              <p className="mt-3 text-xs text-text-muted">
                Enviada por {s.uploader.displayName || s.uploader.email} •{' '}
                {new Date(s.createdAt).toLocaleDateString('pt-BR')}
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button onClick={handleDownload} loading={downloading}>
                <Download size={16} /> Baixar .zip
              </Button>
              {canEdit && (
                <>
                  <Button variant="secondary" onClick={() => nav(`/skills/${s.id}/editar`)}>
                    <Pencil size={16} /> Editar
                  </Button>
                  <Button variant="danger" onClick={handleDelete} loading={remove.isPending}>
                    <Trash2 size={16} /> Excluir
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardHeader>
        <CardBody className="space-y-6">
          <section>
            <h2 className="text-lg font-semibold mb-2">Como usar</h2>
            <div
              className="prose-skill text-text-secondary"
              dangerouslySetInnerHTML={{ __html: marked.parse(s.howToUse) as string }}
            />
          </section>
          {s.keyPoints && (
            <section>
              <h2 className="text-lg font-semibold mb-2">Principais pontos</h2>
              <div
                className="prose-skill text-text-secondary"
                dangerouslySetInnerHTML={{ __html: marked.parse(s.keyPoints) as string }}
              />
            </section>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
